import React, { useEffect, useState } from 'react';
import { Agente, Sitio } from '../types';
import { getAgentes } from '../services/agentes.service';
import { getSitios } from '../services/sitios.service';
import api from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';

interface Relacion {
  _id: string;
  agente: Agente | string;
  sitio: Sitio | string;
}

const RelacionesPage: React.FC = () => {
  const [agentes, setAgentes] = useState<Agente[]>([]);
  const [sitios, setSitios] = useState<Sitio[]>([]);
  const [relaciones, setRelaciones] = useState<Relacion[]>([]);
  const [loading, setLoading] = useState(true);
  const [agenteId, setAgenteId] = useState('');
  const [sitioId, setSitioId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const cargar = async () => {
    setLoading(true);
    try {
      const [a, s, r] = await Promise.all([
        getAgentes({ page: 1, limit: 1000 }),
        getSitios(),
        api.get('/relaciones/agente-sitio'),
      ]);
      setAgentes(a.data);
      setSitios(s.data);
      setRelaciones(r.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { cargar(); }, []);

  const handleCrear = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.post('/relaciones/agente-sitio', { agenteId, sitioId });
      setAgenteId('');
      setSitioId('');
      cargar();
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (id: string) => {
    if (!window.confirm('¿Eliminar esta relación?')) return;
    try {
      await api.delete(`/relaciones/agente-sitio/${id}`);
      cargar();
    } catch {}
  };

  const getNombre = (obj: any, lista: any[], campo: string) => {
    if (typeof obj === 'object') return obj[campo];
    const found = lista.find(x => x._id === obj);
    return found?.[campo] || obj;
  };

  return (
    <>
      <h1 className="page-title">Administracion Relaciones</h1>

      <div className="card">
        <h2 className="card-title">Asignar agente a sitio</h2>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={handleCrear}>
          <div className="form-grid">
            <div className="form-group">
              <label>Agente</label>
              <select value={agenteId} onChange={e => setAgenteId(e.target.value)} required>
                <option value="">Seleccionar Colaborador...</option>
                {agentes.map(a => (
                  <option key={a._id} value={a._id}>{a.nombre} — Documento {(a as any).documento || a.dni || '-'}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Sitio</label>
              <select value={sitioId} onChange={e => setSitioId(e.target.value)} required>
                <option value="">Seleccionar sitio...</option>
                {sitios.map(s => (
                  <option key={s._id} value={s._id}>{s.nombre}</option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Guardando...' : 'Crear relación'}
          </button>
        </form>
      </div>

      <div className="card">
        <h2 className="card-title">Relaciones agente ↔ sitio</h2>
        {loading ? (
          <div className="loading">Cargando...</div>
        ) : relaciones.length === 0 ? (
          <div className="empty-state">No hay relaciones registradas.</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Agente</th>
                  <th>DNI</th>
                  <th>Sitio</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {relaciones.map(r => (
                  <tr key={r._id}>
                    <td>{getNombre(r.agente, agentes, 'nombre')}</td>
                    <td>{typeof r.agente === 'object' ? ((r.agente as any).documento || (r.agente as Agente).dni || '-') : '-'}</td>
                    <td>{getNombre(r.sitio, sitios, 'nombre')}</td>
                    <td>
                      <button className="btn btn-danger btn-sm" onClick={() => handleEliminar(r._id)}>
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default RelacionesPage;
